<template>
  <div class="p-8 max-w-7xl mx-auto space-y-8 bg-[#f8fafc] min-h-full">
    
    <!-- En-tête de la page -->
    <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200/60 shadow-sm">
      <div>
        <h1 class="text-xl font-bold text-slate-900">Gestion des Patients</h1>
        <p class="text-xs text-slate-400 mt-0.5">Consultez, filtrez et suivez l'historique complet des dossiers patients.</p>
      </div>
      <div class="flex items-center gap-3">
        <div class="px-3.5 py-2 rounded-xl bg-blue-50 text-blue-600 text-xs font-semibold flex items-center gap-2 border border-blue-100/50">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{{ currentTime }}</span>
        </div>
      </div>
    </div>

    <!-- Barre de recherche moderne -->
    <div class="relative">
      <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>
      <input 
        type="text" 
        v-model="searchTerm" 
        @input="filterPatients" 
        placeholder="Rechercher un patient par nom ou prénom..." 
        class="w-full pl-11 pr-4 py-3 bg-white border border-slate-200/60 rounded-2xl text-slate-800 placeholder-slate-400 text-xs font-medium shadow-sm focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all"
      />
    </div>
    
    <!-- Tableau des patients -->
    <div class="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
      <div class="p-6 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h3 class="text-base font-bold text-slate-900">Liste des patients enregistrés</h3>
          <p class="text-xs text-slate-400">Affichage par ordre chronologique décroissant</p>
        </div>
        <span class="text-xs font-semibold text-slate-600 bg-slate-100 px-3 py-1 rounded-xl">
          {{ filteredPatients.length }} résultat(s)
        </span>
      </div>

      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-slate-100 text-left">
          <thead class="bg-slate-50/50">
            <tr>
              <th class="px-6 py-3.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Date</th>
              <th class="px-6 py-3.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Nom</th>
              <th class="px-6 py-3.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Prénom</th>
              <th class="px-6 py-3.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Âge</th>
              <th class="px-6 py-3.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Numéro</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100 text-xs">
            <tr v-if="paginatedPatients.length === 0">
              <td colspan="5" class="px-6 py-12 text-center text-slate-400 italic">
                Aucun patient trouvé.
              </td>
            </tr>
            <tr v-for="patient in paginatedPatients" :key="patient._id || patient.id" class="hover:bg-slate-50/60 transition-colors">
              <td class="px-6 py-4 whitespace-nowrap text-slate-500 font-mono">{{ patient.date }}</td>
              <td class="px-6 py-4 whitespace-nowrap font-bold text-slate-900">{{ patient.lastName }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-slate-600">{{ patient.firstName }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-slate-600">{{ patient.age }} ans</td>
              <td class="px-6 py-4 whitespace-nowrap font-mono text-slate-500">{{ patient.phoneNumber }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    
    <!-- Pagination -->
    <div class="flex justify-between items-center bg-white p-4 rounded-2xl border border-slate-200/60 shadow-sm">
      <button 
        @click="previousPage" 
        :disabled="currentPage === 1" 
        class="px-4 py-2 text-xs font-semibold text-slate-700 bg-slate-100 rounded-xl hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" 
      >
        Précédent
      </button>
      <span class="text-xs font-semibold text-slate-500">
        Page <span class="text-slate-900 font-bold">{{ currentPage }}</span> sur <span class="text-slate-900 font-bold">{{ totalPages || 1 }}</span>
      </span>
      <button 
        @click="nextPage" 
        :disabled="currentPage === totalPages || totalPages === 0" 
        class="px-4 py-2 text-xs font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-md shadow-blue-500/20" 
      >
        Suivant
      </button>
    </div>

  </div>
</template>

<script>
export default {
  name: 'AdminPatient',
  data() {
    return {
      patients: [], 
      searchTerm: '', 
      filteredPatients: [], 
      currentPage: 1, 
      itemsPerPage: 10 
    };
  },
  computed: {
    currentTime() {
      const now = new Date();
      return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
    totalPages() {
      return Math.ceil(this.filteredPatients.length / this.itemsPerPage);
    },
    paginatedPatients() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredPatients.slice(start, start + this.itemsPerPage);
    }
  },
  created() {
    this.fetchPatients();
  },
  methods: {
    async fetchPatients() {
      try {
        const response = await fetch('https://thierry-paradis-medecin-743b8e1c5de0.herokuapp.com/patients');
        const data = await response.json();
        
        // Tri décroissant strict (du plus récent au plus ancien)
        this.patients = data.sort((a, b) => {
          const dateA = new Date(a.date || a.createdAt || 0);
          const dateB = new Date(b.date || b.createdAt || 0);
          if (dateA.getTime() !== dateB.getTime()) {
            return dateB - dateA; 
          }
          return (b._id || b.id || '').localeCompare(a._id || a.id || '');
        });

        this.filteredPatients = this.patients; 
      } catch (error) {
        console.error('Erreur lors de la récupération des patients:', error);
      }
    },
    filterPatients() {
      this.filteredPatients = this.patients.filter(patient => {
        const fullName = `${patient.lastName || ''} ${patient.firstName || ''}`.toLowerCase();
        return fullName.includes(this.searchTerm.toLowerCase());
      });
      this.currentPage = 1; 
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    }
  },
  mounted() {
    setInterval(() => {
      this.$forceUpdate(); 
    }, 60000);
  },
};
</script>